import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { FinanceArea, FinanceModal } from "./types";

export function FinanceAreaAction({
	area,
	onModal,
}: {
	area: FinanceArea;
	onModal: (modal: FinanceModal) => void;
}) {
	return (
		<div data-component="finance-area-actions" className="flex flex-wrap items-center gap-2">
			{(area === "overview" || area === "receipts") && (
				<Button size="sm" onClick={() => onModal({ kind: "receipt" })}>
					<Plus />
					Registrar recebimento
				</Button>
			)}
			{area === "bills" && (
				<Button size="sm" onClick={() => onModal({ kind: "bill" })}>
					<Plus />
					Nova conta
				</Button>
			)}
			{area === "bank" && <BankSyncAction />}
			{area === "reconciliation" && (
				<>
					<Button
						size="sm"
						variant="outline"
						data-slot="rede-report-import"
						onClick={() => onModal({ kind: "rede-sales-import" })}
					>
						<FileUp />
						Importar vendas
					</Button>
					<AllSyncAction />
				</>
			)}
		</div>
	);
}

function BankSyncAction() {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.integrations.bank.sync.mutationOptions({
			onSuccess: async (result) => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });

				if (!result.synced) {
					toast.error("Não foi possível atualizar o extrato");

					return;
				}

				toast.success("Extrato atualizado");
			},
			onError: () => toast.error("Não foi possível atualizar o extrato"),
		}),
	);

	return (
		<Button size="sm" variant="outline" disabled={isPending} onClick={() => mutate({})}>
			<RefreshCw className={cn(isPending && "animate-spin")} />
			{isPending && "Atualizando"}
			{!isPending && "Atualizar extrato"}
		</Button>
	);
}

function AllSyncAction() {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.integrations.sync.mutationOptions({
			onSuccess: async (result) => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });

				if (!result.bank.synced || !result.cards.synced) {
					toast.error("Nem todos os dados foram atualizados");

					return;
				}

				toast.success("Dados atualizados");
			},
			onError: () => toast.error("Não foi possível atualizar os dados"),
		}),
	);

	return (
		<Button size="sm" variant="outline" disabled={isPending} onClick={() => mutate({})}>
			<RefreshCw className={cn(isPending && "animate-spin")} />
			{isPending && "Atualizando"}
			{!isPending && "Atualizar dados"}
		</Button>
	);
}
